/* Canvello v8 — the product page (Task 4.2). sections/cv-product.liquid.
 *
 * <cv-product> wires what the server rendered:
 *   - Variant picker: one radio group per option. The chosen variant sets the
 *     form's id, price (compare-at, unit), SKU, stock line, the image shown,
 *     the bar, and ?variant= in the address (replaceState, no history
 *     entries). Values that, with the other choices, match no variant or
 *     none in stock are struck through and say why to screen readers; a
 *     missing combination disables Add to bag and says so. The variants are
 *     the JSON at the end of the section.
 *   - Gallery: thumbs (one tab stop, arrows move and show, mirrored in RTL;
 *     Home/End), previous/next, swipe on touch, the Zoom button (arrow keys
 *     pan, Escape leaves). Press-and-hold zoom is cv-motion.js's.
 *   - Quantity: the stepper and v8's presets (Single, The pair, Set of four:
 *     tiles that each show their line's total), held to the stock when it
 *     can't be oversold; Add to bag carries the line's total, in the shop's
 *     money format (read off a sample, data-money).
 *   - Add to bag: both buttons (the column's and the bar's) submit the one
 *     form; it posts /cart/add.js, fires cart:updated with the cart (the
 *     header's bag follows) and announces it. Without JS the form posts and
 *     the visitor lands on the cart.
 *   - The bar (#cvPdpBar): cv-motion.js shows it once #cvBuy has scrolled
 *     away; here it leaves the tab order while hidden, and lifts other
 *     widgets fixed to the bottom corners (WhatsApp, TrustedSite) clear of it.
 *   - Delivery: an arrival range from the shipping policy's business days
 *     and cut-off, in the shop's time zone, where the section shows one.
 *   - "Back to results" shows when the visitor came from a collection or a
 *     search on this shop, and goes back to it.
 *   - Rugs (Task 4.3): an option with one value is a fact with a hidden
 *     input, not a radio group; sizes show as the variant's "label".
 *   - Magic 360 (Task 4.3): the app's spin viewer is moved from its own
 *     gallery (hidden at the end of the page) into the first slide; arrow
 *     keys turn it, its full-screen button gets a name, its strings are the
 *     theme's. Without the app the 360 slide and thumb are removed.
 */
(() => {
  if (customElements.get('cv-product')) return;

  const rtl = () => document.documentElement.dir === 'rtl';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pad = (n) => String(n).padStart(2, '0');
  const fill = (tpl, map) => Object.keys(map).reduce((s, k) => s.split(`[${k}]`).join(map[k]), tpl || '');

  // The shop's money format, from the server's rendering of 1,234,567.89:
  // what comes before and after the digits, the thousands mark and the
  // decimal mark (none for currencies without cents). Whole amounts drop
  // their zeros, as money_without_trailing_zeros does.
  const moneyFormat = (sample) => {
    const m = /^(\D*)(\d[\d\s.,'\u00a0\u202f]*\d)(\D*)$/.exec((sample || '').trim());
    if (!m) return null;
    const body = m[2];
    const dec = /\D\d{2}$/.test(body) ? body.charAt(body.length - 3) : '';
    const head = dec ? body.slice(0, -3) : body;
    const group = (/\D/.exec(head) || [''])[0];
    return (cents) => {
      const whole = Math.floor(cents / 100);
      const frac = String(Math.round(cents % 100)).padStart(2, '0');
      const int = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, group);
      return m[1] + int + (dec && frac !== '00' ? dec + frac : '') + m[3];
    };
  };

  class CVProduct extends HTMLElement {
    connectedCallback() {
      if (this.cvReady) return;
      this.cvReady = true;
      const $ = (s) => this.querySelector(s);
      this.form = $('[data-cv-pdp-form]');
      this.idInput = $('[data-cv-pdp-id]');
      // Shop Pay Installments' own form and the Buy it now button follow the variant.
      this.termsInput = $('[data-cv-pdp-terms-id]');
      this.express = $('[data-cv-pdp-express]');
      // In option order: a rug's one-value options (facts) follow the pickers.
      this.sets = Array.from(this.querySelectorAll('[data-cv-pdp-opt]')).sort((a, b) => a.dataset.cvPdpOpt - b.dataset.cvPdpOpt);
      this.adoptSpin();
      this.slides = Array.from(this.querySelectorAll('[data-cv-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-cv-thumb]'));
      this.plate = $('[data-cv-pdp-plate]');
      this.qty = $('[data-cv-pdp-qty-input]');
      this.bar = document.getElementById('cvPdpBar');
      this.money = moneyFormat(this.dataset.money);
      try {
        this.variants = JSON.parse($('[data-cv-pdp-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }
      this.index = Math.max(0, this.slides.findIndex((s) => !s.hidden));
      if (this.idInput) this.idInput.disabled = false;

      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-cv-pdp-opt]')) this.update();
        else if (e.target.closest('[data-cv-pdp-presets]')) this.setQty(Number(e.target.value), true);
        else if (e.target === this.qty) this.setQty(Number(this.qty.value));
      });
      this.addEventListener('click', (e) => this.onClick(e));
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));

      this.initGallery();
      if (this.spinGone) this.show(this.index);
      this.initBar();
      this.initBack();
      this.estimate();
      this.update(true);
    }

    /* ---- Variant picker --------------------------------------------------- */

    chosen() {
      return this.sets.map((fs) => {
        const c = fs.querySelector('input:checked, [data-cv-pdp-fixed]');
        return c ? c.value : null;
      });
    }

    find(values) {
      return this.variants.find((v) => v.options.every((o, i) => o === values[i]));
    }

    update(first = false) {
      const chosen = this.chosen();
      const variant = this.sets.length ? this.find(chosen) : this.variants[0];
      this.variant = variant || null;
      // Task 7.5: the trade price, offer dialog and share link follow the variant (cv-talk.js).
      this.dispatchEvent(new CustomEvent('cv:variant', { bubbles: true, detail: { variant: this.variant, first } }));

      this.sets.forEach((fs, i) => {
        fs.querySelectorAll('input[type="radio"]').forEach((input) => {
          const test = chosen.slice();
          test[i] = input.value;
          const match = this.find(test);
          const label = input.closest('label');
          label.toggleAttribute('data-off', !match || !match.available);
          const note = label.querySelector('[data-cv-pdp-off-note]');
          if (note) note.textContent = !match ? ` (${this.dataset.unavailableNote})` : !match.available ? ` (${this.dataset.soldNote})` : '';
          const price = label.querySelector('[data-cv-pdp-value-price]');
          if (price) price.textContent = match ? match.trade || match.price : '';
        });
        const prices = Array.from(fs.querySelectorAll('[data-cv-pdp-value-price]'), (el) => el.textContent).filter(Boolean);
        fs.toggleAttribute('data-same-price', new Set(prices).size < 2);
        const shown = fs.querySelector('[data-cv-pdp-opt-value]');
        if (shown) shown.textContent = chosen[i] || '';
      });

      const adds = this.querySelectorAll('[data-cv-pdp-add]');
      const setAdd = (disabled, text) => {
        this.renderSum();
        adds.forEach((b) => {
          b.disabled = disabled;
          const l = b.querySelector('[data-cv-pdp-add-label]');
          if (l && b.getAttribute('aria-busy') !== 'true') l.textContent = text;
        });
      };

      const stock = this.querySelector('[data-cv-pdp-stock]');
      const head = this.querySelector('[data-cv-pdp-stock-head]');
      const meter = this.querySelector('[data-cv-pdp-stock-bar]');
      const barMeta = this.bar && this.bar.querySelector('[data-cv-pdp-bar-meta]');
      const ships = this.querySelector('[data-cv-pdp-ships]');

      if (!variant) {
        setAdd(true, this.dataset.unavailableShort);
        stock.dataset.tone = 'out';
        head.textContent = this.dataset.unavailable;
        meter.style.inlineSize = '0%';
        if (ships) ships.hidden = true;
        if (this.express) this.express.hidden = true;
        if (barMeta) barMeta.textContent = this.dataset.unavailable;
        if (!first) this.say(this.dataset.unavailable);
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;
      if (this.express) this.express.hidden = !variant.available;
      if (this.termsInput && this.termsInput.value !== String(variant.id)) {
        this.termsInput.value = variant.id;
        // shopify-payment-terms listens for the change on its form's id input.
        this.termsInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      setAdd(!variant.available, variant.available ? this.dataset.add : this.dataset.sold);
      stock.dataset.tone = variant.tone;
      head.textContent = variant.stock;
      meter.style.inlineSize = variant.pct + '%';
      // The handling time is for stock on hand.
      if (ships) ships.hidden = variant.tone === 'order' || variant.tone === 'out';

      const sku = this.querySelector('[data-cv-pdp-sku]');
      const skuWrap = this.querySelector('[data-cv-pdp-sku-wrap]');
      if (sku) sku.textContent = variant.sku || '';
      if (skuWrap) skuWrap.hidden = !variant.sku;
      const crumbSku = this.querySelector('[data-cv-pdp-crumb-sku]');
      if (crumbSku && variant.sku) crumbSku.textContent = variant.sku;

      if (this.qty) {
        if (variant.max > 0) this.qty.max = variant.max;
        else this.qty.removeAttribute('max');
        if (!first) this.setQty(Number(this.qty.value));
      }

      if (this.bar) {
        const multi = this.sets.length > 0;
        barMeta.replaceChildren();
        if (multi) {
          const b = document.createElement('bdi');
          const label = variant.label || variant.title;
          // Feet and inches alone have no direction of their own.
          b.dir = /\p{L}/u.test(label) ? 'auto' : 'ltr';
          b.textContent = label;
          barMeta.append(b, ' · ');
        }
        barMeta.append(variant.stock);
        const bp = this.bar.querySelector('[data-cv-pdp-bar-price]');
        if (bp) bp.textContent = variant.trade || variant.price;
      }

      if (first) return;

      this.renderPrice(variant);
      if (variant.media) {
        const i = this.slides.findIndex((s) => s.dataset.media === String(variant.media));
        if (i >= 0) this.show(i);
      }
      this.syncBarImage();
      if (this.variants.length > 1) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      }
      this.say([variant.label || variant.title, variant.price, variant.stock].join(' · '));
    }

    renderPrice(v) {
      const box = this.querySelector('[data-cv-pdp-price]');
      if (!box) return;
      const hidden = (text) => {
        const s = document.createElement('span');
        s.className = 'visually-hidden';
        s.textContent = text;
        return s;
      };
      const now = document.createElement('span');
      now.className = 'cv-pdp__now cv-price-display';
      now.textContent = v.price;
      // Spaced as the server writes it, so screen readers hear "Sale price $99".
      const parts = [hidden(v.was ? this.dataset.saleLabel : this.dataset.priceLabel), ' ', now];
      if (v.was) {
        // The compare-at price over the saving, as the server writes them.
        const deal = document.createElement('span');
        deal.className = 'cv-pdp__deal';
        const was = document.createElement('s');
        was.className = 'cv-pdp__was';
        was.textContent = v.was;
        deal.append(hidden(this.dataset.regularLabel), ' ', was);
        const off = v.was_cents > 0 ? Math.round(((v.was_cents - v.cents) / v.was_cents) * 100) : 0;
        if (off > 0) {
          const chip = document.createElement('span');
          chip.className = 'cv-pdp__off cv-micro';
          chip.textContent = fill(this.dataset.saveLabel, { percent: off });
          deal.append(chip);
        }
        parts.push(' ', deal);
      }
      if (v.unit) {
        const unit = document.createElement('span');
        unit.className = 'cv-pdp__unit cv-meta';
        unit.append(hidden(this.dataset.unitLabel), ' ', v.unit);
        parts.push(' ', unit);
      }
      box.replaceChildren(...parts);
    }

    // Add to bag's total (the price × the quantity) and each preset's, hidden
    // while the variant can't be had (the label says why).
    // With pair and set savings (data-bundle "2:5,4:10", the bag's automatic
    // discounts), a line of 2 or 4+ is shown at its saving, the full sum struck.
    renderSum() {
      const v = this.variant;
      const tiers = (this.dataset.bundle || '')
        .split(',')
        .map((t) => t.split(':').map(Number))
        .filter((t) => t[0] > 1 && t[1] > 0)
        .sort((x, y) => y[0] - x[0]);
      const off = (n) => (tiers.find((t) => n >= t[0]) || [0, 0])[1];
      // A trade account's lines at its trade price (trade_cents), as the bag charges them.
      const base = v && (v.trade_cents || v.cents);
      const cents = (n) => Math.round((base * n * (100 - off(n))) / 100);
      const line = (n) => (this.money ? this.money(cents(n)) : v.trade || v.price);
      this.querySelectorAll('[data-cv-pdp-preset-sum]').forEach((el) => {
        el.hidden = !v || !v.available;
        if (v) el.textContent = line(Number(el.dataset.cvPdpPresetSum) || 1);
      });
      this.querySelectorAll('[data-cv-pdp-preset-was]').forEach((el) => {
        el.hidden = !v || !v.available;
        if (v && this.money) el.textContent = this.money(base * (Number(el.dataset.cvPdpPresetWas) || 1));
      });
      const sum = this.querySelector('[data-cv-pdp-sum]');
      if (!sum) return;
      const q = this.qty ? Math.max(1, Number(this.qty.value) || 1) : 1;
      sum.hidden = !v || !v.available;
      if (v) sum.textContent = line(q);
    }

    say(text) {
      const live = this.querySelector('[data-cv-pdp-live]');
      if (!live) return;
      live.textContent = '';
      requestAnimationFrame(() => (live.textContent = text));
    }

    /* ---- Quantity --------------------------------------------------------- */

    setQty(n, fromPreset = false) {
      if (!this.qty) return;
      const max = Number(this.qty.max) || Infinity;
      const note = this.querySelector('[data-cv-pdp-qty-note]');
      let q = Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
      if (q > max) {
        q = max;
        if (note) {
          note.textContent = fill(this.dataset.qtyMax, { count: max });
          note.hidden = false;
        }
      } else if (note) {
        note.hidden = true;
      }
      this.qty.value = q;
      const presets = this.querySelectorAll('[data-cv-pdp-presets] input');
      presets.forEach((r) => {
        const v = Number(r.value);
        r.closest('label').toggleAttribute('data-off', v > max);
        if (!fromPreset) r.checked = v === q;
      });
      if (fromPreset) presets.forEach((r) => (r.checked = Number(r.value) === q));
      this.renderSum();
    }

    onClick(e) {
      const step = e.target.closest('[data-cv-pdp-qty]');
      if (step) {
        this.setQty(Number(this.qty.value) + Number(step.dataset.cvPdpQty));
        return;
      }
      const nav = e.target.closest('[data-cv-pdp-step]');
      if (nav) {
        this.step(Number(nav.dataset.cvPdpStep), true);
        return;
      }
      const thumb = e.target.closest('[data-cv-thumb]');
      if (thumb) {
        this.show(Number(thumb.dataset.cvThumb), { announce: true });
        return;
      }
      if (e.target.closest('[data-cv-pdp-zoom]')) this.toggleZoom();
      // The kicker's stars open the Reviews panel and move focus to it.
      const rev = e.target.closest('[data-cv-pdp-reviews-link]');
      if (rev) {
        const panel = document.getElementById(rev.getAttribute('href').slice(1));
        if (!panel) return;
        e.preventDefault();
        // A panel opens; the Reviews section (#cvReviews) is only scrolled to.
        if (panel.tagName === 'DETAILS') panel.open = true;
        const head = panel.querySelector('summary, h2[tabindex]');
        panel.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        if (head) head.focus({ preventScroll: true });
      }
    }

    /* ---- Add to bag ------------------------------------------------------- */

    onSubmit(e) {
      if (!window.fetch) return;
      e.preventDefault();
      const adds = Array.from(this.querySelectorAll('[data-cv-pdp-add]'));
      if (!this.variant || !this.variant.available || adds.some((b) => b.getAttribute('aria-busy') === 'true')) return;
      const labels = adds.map((b) => b.querySelector('[data-cv-pdp-add-label]'));
      const note = this.querySelector('[data-cv-pdp-qty-note]');
      adds.forEach((b) => b.setAttribute('aria-busy', 'true'));
      // Busy only while the request runs; "Added" lingers on the label, and
      // a second add (the bar, after a preset) is never locked out.
      clearTimeout(this.labelTimer);
      const settle = (msg, ms) => {
        adds.forEach((b) => b.removeAttribute('aria-busy'));
        labels.forEach((l) => l && (l.textContent = msg));
        this.labelTimer = setTimeout(() => {
          labels.forEach((l) => l && (l.textContent = this.variant && this.variant.available ? this.dataset.add : this.dataset.sold));
        }, ms);
      };
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      fetch(addUrl.replace(/\/?$/, '.js'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(this.form),
      })
        .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
        .then(({ ok, body }) => {
          if (!ok) throw new Error(body.description || body.message || this.dataset.error);
          return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        })
        .then((cart) => {
          if (note) note.hidden = true;
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart, item_count: cart.item_count, source: 'cv-product' } }));
          settle(this.dataset.added, 2200);
          this.say(this.dataset.addedLive);
        })
        .catch((err) => {
          const msg = (err && err.message) || this.dataset.error;
          if (note) {
            note.textContent = msg;
            note.hidden = false;
          }
          settle(this.dataset.errorShort, 3200);
          this.say(msg);
        });
    }

    /* ---- Magic 360 -------------------------------------------------------- */
    // The app's embed renders a gallery of its own at the end of the page
    // (#magic-360-block) with the spin in it, and starts the viewer when the
    // page is parsed (DOMContentLoaded, after this deferred script). The spin
    // moves into the 360 slide before then; the rest of the app's gallery
    // stays hidden (its stylesheets live in it).

    adoptSpin() {
      const host = this.querySelector('[data-cv-spin-host]');
      if (!host) return;
      const slide = host.closest('[data-cv-spin]');
      const block = document.getElementById('magic-360-block');
      // The page brings a bare link (a.Magic360); the viewer wraps it in a
      // container when it starts.
      const link = block && (block.querySelector('[data-slide-id="spin"] .Magic360') || block.querySelector('.Magic360'));
      const box = link && (link.closest('.Magic360-container') || link);
      const M = window.Magic360;
      if (block) block.hidden = true;
      if (!box || !M) {
        // No viewer (the app or its embed is off): the photos alone.
        this.spinGone = !slide.hidden;
        slide.remove();
        const thumb = this.querySelector('[data-cv-thumb-spin]');
        if (thumb) thumb.remove();
        this.querySelectorAll('[data-cv-slide]').forEach((el, i) => (el.dataset.cvSlide = i));
        this.querySelectorAll('[data-cv-thumb]').forEach((el, i) => (el.dataset.cvThumb = i));
        const first = this.querySelector('[data-cv-thumb]');
        if (this.spinGone && first) first.tabIndex = 0;
        return;
      }
      const d = this.dataset;
      M.lang = Object.assign(M.lang || {}, {
        'loading-text': d.tSpinLoading,
        'fullscreen-loading-text': d.tSpinLoadingFull,
        'hint-text': d.tSpinDrag,
        'mobile-hint-text': d.tSpinSwipe,
      });
      host.replaceChildren(box);
      this.spinEl = link;
      // The viewer's link (href="#") is not a stop; the slide is.
      if (this.spinEl) this.spinEl.tabIndex = -1;
      // A viewer started inside a hidden slide measures 0: restart it when shown.
      this.spinLive = !slide.hidden;
      // The full-screen button arrives when the viewer starts.
      const name = () => {
        host.querySelectorAll('.m360-icon-fullscreen-open:not([aria-label])').forEach((b) => b.setAttribute('aria-label', d.tSpinFull));
      };
      name();
      new MutationObserver(name).observe(host, { childList: true, subtree: true });
      // Full screen is the browser's own (Escape leaves it). The app leaves
      // focus on the page behind: it goes to the named close button, and
      // back to the open button after.
      document.addEventListener('fullscreenchange', () => {
        const on = document.fullscreenElement;
        if (on && !on.closest('.Magic360-fullscreen') && !on.querySelector('.Magic360-fullscreen')) return;
        if (on) {
          // The app adds its close button a few frames after the change
          // (~300ms): wait for it, up to 2s.
          const t0 = performance.now();
          const find = () => {
            const shut = document.querySelector('.Magic360-fullscreen .m360-icon-fullscreen-close');
            if (shut) {
              shut.setAttribute('aria-label', d.tSpinClose);
              shut.focus();
            } else if (document.fullscreenElement && performance.now() - t0 < 2000) requestAnimationFrame(find);
          };
          find();
        } else if (this.spinOpen) {
          // The open button comes back a few frames later too.
          const back = this.spinOpen;
          const t0 = performance.now();
          const ret = () => {
            const a = document.activeElement;
            const lost = !a || a === document.body || !!a.closest('.Magic360-fullscreen');
            if (!lost) return;
            const shown = back.checkVisibility ? back.checkVisibility() : back.offsetParent !== null;
            if (shown) back.focus();
            else if (performance.now() - t0 < 2000) requestAnimationFrame(ret);
          };
          ret();
        }
      });
      host.addEventListener('click', (e) => {
        const open = e.target.closest('.m360-icon-fullscreen-open');
        if (open) this.spinOpen = open;
      }, true);
      host.addEventListener('keydown', (e) => {
        // A turn of the piece, not a reading direction: not mirrored in RTL.
        const step = { ArrowLeft: -1, ArrowRight: 1 }[e.key];
        if (!step || e.target !== host || !this.spinEl || !window.Magic360) return;
        e.preventDefault();
        // The keys take over from the app's opening turn.
        window.Magic360.pause(this.spinEl);
        window.Magic360.spin(this.spinEl, step);
      });
    }

    /* ---- Gallery ---------------------------------------------------------- */

    initGallery() {
      const rail = this.querySelector('[data-cv-pdp-thumbs]');
      if (rail) {
        rail.addEventListener('keydown', (e) => {
          const keys = { ArrowUp: -1, ArrowDown: 1, ArrowLeft: rtl() ? 1 : -1, ArrowRight: rtl() ? -1 : 1 };
          let next = null;
          if (e.key in keys) next = this.index + keys[e.key];
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = this.slides.length - 1;
          if (next === null) return;
          e.preventDefault();
          next = (next + this.slides.length) % this.slides.length;
          this.show(next, { focus: true, announce: true });
        });
      }
      if (!this.plate) return;
      // Swipe on touch; a press that doesn't move is left to the zoom.
      let x0 = null;
      let y0 = 0;
      this.plate.addEventListener('pointerdown', (e) => {
        // A drag on the 360 slide turns the piece.
        if (e.pointerType !== 'touch' || e.target.closest('button, [data-cv-spin-host]')) return;
        x0 = e.clientX;
        y0 = e.clientY;
      });
      this.plate.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) this.step((dx < 0) !== rtl() ? 1 : -1, true);
      });
      this.plate.addEventListener('pointercancel', () => (x0 = null));
      // The Zoom button and the hold-to-zoom share one state on the plate.
      const btn = this.querySelector('[data-cv-pdp-zoom]');
      if (btn) {
        new MutationObserver(() => btn.setAttribute('aria-pressed', String(this.plate.hasAttribute('data-cv-zoom')))).observe(this.plate, {
          attributes: true,
          attributeFilter: ['data-cv-zoom'],
        });
        btn.addEventListener('keydown', (e) => {
          if (!this.plate.hasAttribute('data-cv-zoom')) return;
          const moves = { ArrowLeft: [-10, 0], ArrowRight: [10, 0], ArrowUp: [0, -10], ArrowDown: [0, 10] };
          if (e.key === 'Escape') {
            e.preventDefault();
            this.toggleZoom(false);
          } else if (moves[e.key]) {
            e.preventDefault();
            const at = (v) => parseFloat(this.plate.style.getPropertyValue(v)) || 50;
            const clampPct = (n) => Math.min(100, Math.max(0, n)).toFixed(1) + '%';
            this.plate.style.setProperty('--cv-zx', clampPct(at('--cv-zx') + moves[e.key][0]));
            this.plate.style.setProperty('--cv-zy', clampPct(at('--cv-zy') + moves[e.key][1]));
          }
        });
        btn.addEventListener('blur', () => this.keyZoom && this.toggleZoom(false));
      }
    }

    toggleZoom(on) {
      if (!this.plate) return;
      const next = on === undefined ? !this.plate.hasAttribute('data-cv-zoom') : on;
      this.keyZoom = next;
      if (next) {
        this.plate.style.setProperty('--cv-zx', '50%');
        this.plate.style.setProperty('--cv-zy', '50%');
      }
      this.plate.toggleAttribute('data-cv-zoom', next);
    }

    step(d, announce = false) {
      const n = this.slides.length;
      if (n < 2) return;
      this.show((this.index + d + n) % n, { announce });
    }

    show(i, { focus = false, announce = false } = {}) {
      if (!this.slides[i]) return;
      this.toggleZoom(false);
      this.index = i;
      this.slides.forEach((s, j) => {
        s.hidden = j !== i;
        if (j !== i) s.querySelectorAll('video').forEach((v) => v.pause && v.pause());
      });
      this.thumbs.forEach((t, j) => {
        const on = j === i;
        t.tabIndex = on ? 0 : -1;
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      const thumb = this.thumbs[i];
      if (thumb) {
        if (focus) thumb.focus({ preventScroll: true });
        this.reveal(thumb);
      }
      const n = this.slides.length;
      const spin = this.slides[i].hasAttribute('data-cv-spin');
      if (this.plate) {
        // On the 360 slide a press turns the piece: no hold-to-zoom, no Zoom button.
        this.plate.toggleAttribute('data-zoom', !spin);
        if (this.plate.dataset.curSpin) this.plate.dataset.cur = spin ? this.plate.dataset.curSpin : this.plate.dataset.curZoom;
        const zoomBtn = this.querySelector('[data-cv-pdp-zoom]');
        if (zoomBtn) zoomBtn.hidden = spin;
      }
      if (spin && !this.spinLive && this.spinEl && window.Magic360) {
        window.Magic360.stop(this.spinEl);
        window.Magic360.start(this.spinEl);
        this.spinLive = true;
      }
      const label = this.querySelector('[data-cv-pdp-plate-label]');
      if (label) {
        const pos = Number(this.dataset.colourPos);
        const name = pos > 0 && this.variant ? this.variant.options[pos - 1] : '';
        if (spin) label.textContent = this.dataset.tSpin;
        else label.textContent = name ? fill(this.dataset.tPlateNamed, { n: pad(i + 1), name }) : fill(this.dataset.tPlate, { n: pad(i + 1) });
      }
      const count = this.querySelector('[data-cv-pdp-count]');
      if (count) count.textContent = `${pad(i + 1)} / ${pad(n)}`;
      if (announce) {
        const live = this.querySelector('[data-cv-pdp-image-live]');
        if (live) {
          live.textContent = '';
          requestAnimationFrame(() => (live.textContent = fill(this.dataset.imageLive, { n: i + 1, total: n })));
        }
      }
    }

    // Scroll the rail, not the page, to bring a thumb into view.
    reveal(thumb) {
      const rail = thumb.closest('[data-cv-pdp-thumbs]');
      if (!rail) return;
      const r = rail.getBoundingClientRect();
      const t = thumb.getBoundingClientRect();
      let dx = 0;
      let dy = 0;
      if (t.top < r.top) dy = t.top - r.top - 6;
      else if (t.bottom > r.bottom) dy = t.bottom - r.bottom + 6;
      if (t.left < r.left) dx = t.left - r.left - 6;
      else if (t.right > r.right) dx = t.right - r.right + 6;
      if (dx || dy) rail.scrollBy({ left: dx, top: dy, behavior: reduce ? 'auto' : 'smooth' });
    }

    syncBarImage() {
      if (!this.bar) return;
      const img = this.bar.querySelector('[data-cv-pdp-bar-img]');
      const src = this.thumbs[this.index] && this.thumbs[this.index].querySelector('img');
      if (!img || !src) return;
      img.srcset = src.srcset;
      img.src = src.src;
    }

    /* ---- The bar ---------------------------------------------------------- */

    initBar() {
      const bar = this.bar;
      if (!bar) return;
      const root = document.documentElement;
      const lifted = new Map();
      // Whatever is fixed along the bottom edge, however deep it is nested:
      // sample points along the edge and climb to the fixed box at each.
      const widgets = () => {
        const found = new Set();
        for (let x = 12; x < innerWidth; x += 40) {
          for (const y of [innerHeight - 6, innerHeight - 30]) {
            for (const hit of document.elementsFromPoint(x, y)) {
              let el = hit;
              while (el && el !== document.body && getComputedStyle(el).position !== 'fixed') el = el.parentElement;
              if (!el || el === document.body || el === bar || bar.contains(el) || el.contains(bar) || lifted.has(el)) continue;
              if (/(^|\s)cv-/.test(typeof el.className === 'string' ? el.className : '')) continue;
              const b = el.getBoundingClientRect();
              if (b.height > 0 && b.height < innerHeight / 2 && b.width < innerWidth * 0.6 && b.bottom > innerHeight - 48) found.add(el);
            }
          }
        }
        return Array.from(found);
      };
      const lift = (on) => {
        const h = on ? Math.round(bar.getBoundingClientRect().height) : 0;
        root.style.setProperty('--cv-pdp-bar-h', h + 'px');
        root.toggleAttribute('data-cv-pdp-bar', on);
        // A marker attribute and a stylesheet rule (cv-product.css), not an
        // inline style: widget scripts rewrite their own style attribute
        // (TrustedSite does, on scroll).
        if (on) {
          widgets().forEach((el) => {
            lifted.set(el, true);
            el.setAttribute('data-cv-lift', '');
          });
        } else {
          lifted.forEach((_, el) => el.removeAttribute('data-cv-lift'));
          lifted.clear();
        }
      };
      const sync = () => {
        const on = bar.hasAttribute('data-cv-on');
        bar.inert = !on;
        lift(on);
      };
      new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['data-cv-on'] });
      // Widgets that load after the bar is up (chat buttons arrive late).
      new MutationObserver(() => bar.hasAttribute('data-cv-on') && lift(true)).observe(document.body, { childList: true });
      sync();
    }

    /* ---- Back to results -------------------------------------------------- */

    initBack() {
      const back = this.querySelector('[data-cv-pdp-back]');
      if (!back || !document.referrer) return;
      let ref;
      try {
        ref = new URL(document.referrer);
      } catch (e) {
        return;
      }
      if (ref.origin !== location.origin || !/\/(collections|search)(\/|$|\?)/.test(ref.pathname + '/')) return;
      if (/\/products\//.test(ref.pathname)) return;
      back.href = ref.pathname + ref.search;
      back.hidden = false;
      back.addEventListener('click', (e) => {
        if (history.length > 1 && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
          e.preventDefault();
          history.back();
        }
      });
    }

    /* ---- Delivery estimate ------------------------------------------------ */
    // Business days from the shipping policy: the order day counts when it is
    // a weekday before the cut-off, then handling + transit. No holidays.

    estimate() {
      if (!this.hasAttribute('data-estimate')) return;
      const out = this.querySelector('[data-cv-pdp-ships]');
      if (!out || !window.Intl || !Intl.DateTimeFormat.prototype.formatToParts) return;
      const [hMin, hMax] = this.dataset.handling.split(',').map(Number);
      const [tMin, tMax] = this.dataset.transit.split(',').map(Number);
      const cutoff = Number(this.dataset.cutoff);
      let parts;
      try {
        parts = new Intl.DateTimeFormat('en-US', {
          timeZone: this.dataset.tz,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          hourCycle: 'h23',
        }).formatToParts(new Date());
      } catch (e) {
        return;
      }
      const get = (t) => Number(parts.find((p) => p.type === t).value);
      const day = new Date(Date.UTC(get('year'), get('month') - 1, get('day')));
      const weekend = (d) => d.getUTCDay() === 0 || d.getUTCDay() === 6;
      const addBiz = (d, n) => {
        const x = new Date(d);
        while (n > 0) {
          x.setUTCDate(x.getUTCDate() + 1);
          if (!weekend(x)) n -= 1;
        }
        return x;
      };
      let base = day;
      if (weekend(base) || get('hour') >= cutoff) base = addBiz(base, 1);
      const from = addBiz(base, hMin + tMin);
      const to = addBiz(base, hMax + tMax);
      const lang = document.documentElement.lang || 'en';
      let fmt;
      try {
        fmt = new Intl.DateTimeFormat(lang.startsWith('ar') ? lang + '-u-nu-latn' : lang, { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' });
      } catch (e) {
        return;
      }
      const range = from.getTime() === to.getTime() ? fmt.format(from) : fmt.formatRange ? fmt.formatRange(from, to) : `${fmt.format(from)} – ${fmt.format(to)}`;
      out.textContent = fill(this.dataset.arrives, { range });
      out.dataset.from = from.toISOString().slice(0, 10);
      out.dataset.to = to.toISOString().slice(0, 10);
    }
  }

  customElements.define('cv-product', CVProduct);
})();
